import { useTranslation } from '@/hooks/use-translation';
import { $apiAllo } from '@/services/api-allo/client';
import { useVoiceCallState } from '@packages/client-sdk-react/src/voice/voice-call-container';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

export type CallTag = {
  key?: string;
  name?: string;
  hex_color?: string | null;
  background_hex_color?: string | null;
};

const SELECTED_KEYS_PREFIX = ['ongoing-call-tags'] as const;
const EMPTY_TAGS: CallTag[] = [];
const EMPTY_SELECTED: string[] = [];

// Optimistic selected-tag state for the ongoing call, stored in the react-query
// cache keyed by call identity. A new call produces a new key, so selections
// reset automatically without an effect.
export const useOngoingCallTags = () => {
  const [callState] = useVoiceCallState();
  const queryClient = useQueryClient();
  const { t } = useTranslation('desktop_ui');

  const callIdentity = callState?.callId ?? '';
  const selectedQueryKey = useMemo(
    () => [...SELECTED_KEYS_PREFIX, callIdentity] as const,
    [callIdentity],
  );

  const { data: tagsData, isLoading: isLoadingTags } = $apiAllo.useQuery(
    'get',
    '/v1/business/tags',
    {},
  );

  const { data: selectedTagKeys = EMPTY_SELECTED } = useQuery<string[]>({
    queryKey: selectedQueryKey,
    queryFn: () => [],
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const setSelected = useCallback(
    (update: (prev: string[]) => string[]) => {
      queryClient.setQueryData<string[]>(selectedQueryKey, (prev = []) => update(prev));
    },
    [queryClient, selectedQueryKey],
  );

  const { mutate: addTagMutate } = $apiAllo.useMutation('put', '/v1/call/ongoing/tag', {
    onError: (_err, variables) => {
      setSelected((prev) => prev.filter((k) => k !== variables.body.tag));
      toast.error(t('components.callTags.error.addFailed', 'Failed to add tag. Please try again.'));
    },
  });

  const { mutate: removeTagMutate } = $apiAllo.useMutation('delete', '/v1/call/ongoing/tag', {
    onError: (_err, variables) => {
      setSelected((prev) =>
        prev.includes(variables.body.tag) ? prev : [...prev, variables.body.tag],
      );
      toast.error(
        t('components.callTags.error.removeFailed', 'Failed to remove tag. Please try again.'),
      );
    },
  });

  const getCallNumbers = useCallback(() => {
    const fromName = callState?.from?.name;
    const fromFallback = fromName
      ? fromName.startsWith('+')
        ? fromName
        : `+${fromName}`
      : undefined;
    const from = callState?.callCtx?.origin_number ?? fromFallback;
    const to = callState?.callCtx?.to;
    return { from, to };
  }, [callState]);

  const addTag = useCallback(
    (tag: string) => {
      if (!callState) {
        return;
      }
      setSelected((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
      const { from, to } = getCallNumbers();
      addTagMutate({ body: { from, to, tag } });
    },
    [callState, addTagMutate, getCallNumbers, setSelected],
  );

  const removeTag = useCallback(
    (tag: string) => {
      if (!callState) {
        return;
      }
      setSelected((prev) => prev.filter((k) => k !== tag));
      const { from, to } = getCallNumbers();
      removeTagMutate({ body: { from, to, tag } });
    },
    [callState, removeTagMutate, getCallNumbers, setSelected],
  );

  const tags = (tagsData?.data ?? EMPTY_TAGS) as CallTag[];

  return {
    tags,
    isLoadingTags,
    selectedTagKeys,
    addTag,
    removeTag,
  };
};
